/* Keep Shopify's native wallet and app validation; reveal their initial UI once. */
(() => {
  const seen = new WeakSet();
  const pending = new Set();
  let observer;

  function initialize(node) {
    if (seen.has(node) || node.dataset.ffPaymentState !== 'loading') return;
    seen.add(node);
    const frames = new Map();
    const observedHosts = new WeakSet();
    const expectsOptions = !!document.querySelector('script[src*="easify-product-options"]');
    let disposed = false;
    let settle;
    let timeout;
    let mutations;
    let resize;
    const state = {node, dispose: () => reveal('pagehide')};

    function reveal(reason) {
      if (disposed) return;
      disposed = true;
      clearTimeout(settle);
      clearTimeout(timeout);
      mutations?.disconnect();
      resize?.disconnect();
      for (const [frame, data] of frames) frame.removeEventListener('load', data.onLoad);
      frames.clear();
      node.dataset.ffPaymentState = 'ready';
      node.dataset.ffPaymentReason = reason;
      pending.delete(state);
    }

    function visibleFrame(frame) {
      const style = getComputedStyle(frame);
      return frame.isConnected && style.display !== 'none' && Number(style.opacity) > 0 && frame.getBoundingClientRect().height > 20;
    }

    function ready() {
      const host = node.querySelector('shopify-accelerated-checkout');
      if (!host || !customElements.get('shopify-accelerated-checkout')) return false;
      if (expectsOptions && !node.querySelector('.tpo-btn-wrapper')) return false;
      if (node.querySelector('.shopify-payment-button__skeleton')) return false;
      if (host.getBoundingClientRect().height < 25) return false;
      return [...frames].every(([frame, data]) => !visibleFrame(frame) || data.loaded);
    }

    function probe() {
      if (disposed) return;
      if (!node.isConnected) return reveal('removed');
      clearTimeout(settle);
      for (const frame of node.querySelectorAll('iframe')) {
        if (frames.has(frame)) continue;
        const data = {loaded: false, onLoad: null};
        data.onLoad = () => { data.loaded = true; probe(); };
        frames.set(frame, data);
        frame.addEventListener('load', data.onLoad);
      }
      const host = node.querySelector('shopify-accelerated-checkout');
      if (host && !observedHosts.has(host)) {
        observedHosts.add(host);
        resize.observe(host);
      }
      if (ready()) settle = setTimeout(() => { if (!disposed && ready()) reveal('stable'); }, 350);
    }

    pending.add(state);
    mutations = new MutationObserver(probe);
    resize = new ResizeObserver(probe);
    mutations.observe(node, {childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'disabled']});
    resize.observe(node);
    timeout = setTimeout(() => reveal('timeout'), 8000);
    customElements.whenDefined('shopify-accelerated-checkout').then(probe);
    probe();
  }

  function scan() {
    for (const state of pending) if (!state.node.isConnected) state.dispose();
    document.querySelectorAll('.ff-payment[data-ff-payment-state="loading"]').forEach(initialize);
  }

  function start() {
    document.documentElement.classList.add('ff-payment-enhanced');
    scan();
    observer?.disconnect();
    observer = new MutationObserver(scan);
    observer.observe(document.getElementById('MainContent') || document.body, {childList: true, subtree: true});
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();
  window.addEventListener('pagehide', () => {
    observer?.disconnect();
    for (const state of [...pending]) state.dispose();
    document.documentElement.classList.remove('ff-payment-enhanced');
  });
  window.addEventListener('pageshow', event => { if (event.persisted) start(); });
})();
